import { djs } from '@duke-hq/djs';
import { Box, Center, Flex, Text, Badge, Spinner, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  MessageCircle,
  FileCheck,
  Clock,
  Calendar,
  TrendingUp,
  TrendingDown,
  FileText,
  User,
} from 'lucide-react';
import type { RESOURCE_STATUSES_TYPE } from '@duke-hq/shared-constants/resourceStatuses';
import { ResourceStatusBadge } from '@duke-hq/ui/components/ResourceStatusBadge/ResourceStatusBadge';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';

type UnitHistoryTimelineItem = RouterOutputs['enrolment']['getUnitHistory'][number];

type UnitHistoryTimelineProps = {
  data: UnitHistoryTimelineItem[] | undefined;
  unitScheduleOverallStatus: RESOURCE_STATUSES_TYPE;
};

export function UnitHistoryTimeline({ data, unitScheduleOverallStatus }: UnitHistoryTimelineProps) {
  if (!data) {
    return (
      <Center w="full" h="200px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (data.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">
          <Trans>No activity recorded for this unit yet</Trans>
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" width="100%" flex={1} minHeight={0}>
      {/* Unit Header */}
      <Flex
        align="center"
        justify="space-between"
        pb={4}
        mb={4}
        borderBottom="1px solid"
        borderColor="gray.300"
        flexShrink={0}
      >
        <Box>
          <Text fontSize="lg" fontWeight="bold">
            {data[0]?.code} - {data[0]?.title}
          </Text>
        </Box>
        <ResourceStatusBadge status={unitScheduleOverallStatus} />
      </Flex>

      {/* Timeline */}
      <Box flex={1} overflowY="auto" pr={2} minHeight={0}>
        <VStack align="stretch" spacing={0}>
          {data.map((item, index) => {
            const isConversation = item.item_type === 'CONVERSATION';
            const isAssessmentSubmission = item.item_type === 'ASSESSMENT_SUBMISSION';
            const isCompleted = item.completed_at != null;
            const isStarted = item.started_at != null;
            const isAssessed = item.assessed_at != null;

            return (
              <Box key={index} position="relative">
                <Flex gap={4} pb={6}>
                  {/* Timeline indicator */}
                  <Flex direction="column" align="center" position="relative">
                    <Box
                      bg={
                        isAssessmentSubmission && isAssessed
                          ? item.outcome === 'SATISFACTORY'
                            ? 'green.500'
                            : item.outcome === 'NOT_YET_SATISFACTORY' || item.outcome === 'REVISION'
                              ? 'red.500'
                              : 'gray.500'
                          : isCompleted
                            ? 'green.500'
                            : isStarted
                              ? 'blue.500'
                              : 'gray.300'
                      }
                      borderRadius="full"
                      p={2}
                      zIndex={1}
                    >
                      {isConversation ? (
                        <MessageCircle size={16} color="white" />
                      ) : isAssessmentSubmission ? (
                        <FileText size={16} color="white" />
                      ) : (
                        <FileCheck size={16} color="white" />
                      )}
                    </Box>
                    {index < data.length - 1 ? (
                      <Box
                        position="absolute"
                        top="40px"
                        bottom="-24px"
                        width="2px"
                        bg="gray.200"
                      />
                    ) : null}
                  </Flex>

                  {/* Content */}
                  <Box flex={1}>
                    <Flex justify="space-between" align="start" mb={2}>
                      <Box>
                        <Flex align="center" gap={2} mb={1}>
                          {isAssessmentSubmission ? (
                            <>
                              <Badge colorScheme="orange" fontSize="xs">
                                <Trans>Submission</Trans>
                              </Badge>
                              {item.outcome ? <ResourceStatusBadge status={item.outcome} /> : null}
                            </>
                          ) : (
                            <>
                              <Badge colorScheme={isConversation ? 'blue' : 'purple'} fontSize="xs">
                                {isConversation ? (
                                  <Trans>Conversation</Trans>
                                ) : (
                                  <Trans>Assessment</Trans>
                                )}
                              </Badge>
                              {item.semester ? (
                                <Badge colorScheme="gray" fontSize="xs">
                                  {t`Semester ${item.semester}`}
                                </Badge>
                              ) : null}

                              {isCompleted ? (
                                <Badge colorScheme="green" fontSize="xs">
                                  <Trans>Completed</Trans>
                                </Badge>
                              ) : null}

                              {!isCompleted && isStarted ? (
                                <Badge colorScheme="blue" fontSize="xs">
                                  <Trans>In Progress</Trans>
                                </Badge>
                              ) : null}
                            </>
                          )}
                        </Flex>
                        <Text fontWeight="semibold" fontSize="md">
                          {item.conversation_title || item.title}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {item.code} - {item.title}
                        </Text>
                      </Box>
                    </Flex>

                    <VStack align="stretch" spacing={1} mt={3}>
                      {isAssessmentSubmission ? (
                        <>
                          {/* Submitted At */}
                          {item.started_at ? (
                            <Flex align="center" gap={2} fontSize="sm" color="gray.600">
                              <Clock size={14} />
                              <Text>
                                <Trans>Submitted:</Trans>{' '}
                                {djs(item.started_at).format('DD MMM YYYY, hh:mm A')}
                              </Text>
                            </Flex>
                          ) : null}

                          {/* Assessed At */}
                          {item.assessed_at ? (
                            <Flex align="center" gap={2} fontSize="sm" color="gray.600">
                              <Clock size={14} />
                              <Text>
                                <Trans>Assessed:</Trans>{' '}
                                {djs(item.assessed_at).format('DD MMM YYYY, hh:mm A')}
                              </Text>
                            </Flex>
                          ) : null}

                          {/* Assessed By */}
                          {item.assessed_by ? (
                            <Flex align="center" gap={2} fontSize="sm" color="gray.600">
                              <User size={14} />
                              <Text>
                                <Trans>Assessed by:</Trans> {item.assessed_by}
                              </Text>
                            </Flex>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {/* Scheduled Start */}
                          {item.scheduled_start_date ? (
                            <Flex align="center" gap={2} fontSize="sm" color="gray.500">
                              <Calendar size={14} />
                              <Text>
                                <Trans>Scheduled Start:</Trans>{' '}
                                {djs(item.scheduled_start_date).format('DD MMM YYYY')}
                              </Text>
                            </Flex>
                          ) : null}

                          {/* Actual Start with comparison */}
                          {isStarted ? (
                            <>
                              <Flex align="center" gap={2} fontSize="sm" color="gray.600">
                                <Clock size={14} />
                                <Text>
                                  <Trans>Actually Started:</Trans>{' '}
                                  {djs(item.started_at).format('DD MMM YYYY, hh:mm A')}
                                </Text>
                              </Flex>
                              <Flex align="center" gap={2} fontSize="xs" ml={5}>
                                {(() => {
                                  const daysDiff = djs(item.started_at).diff(
                                    djs(item.scheduled_start_date),
                                    'day',
                                  );
                                  if (daysDiff < 0) {
                                    return (
                                      <>
                                        <TrendingUp size={12} color="green" />
                                        <Text color="green.600">
                                          {Math.abs(daysDiff)}{' '}
                                          {Math.abs(daysDiff) === 1 ? (
                                            <Trans>day early</Trans>
                                          ) : (
                                            <Trans>days early</Trans>
                                          )}
                                        </Text>
                                      </>
                                    );
                                  } else if (daysDiff > 0) {
                                    return (
                                      <>
                                        <TrendingDown size={12} color="red" />
                                        <Text color="red.600">
                                          {daysDiff}{' '}
                                          {daysDiff === 1 ? (
                                            <Trans>day late</Trans>
                                          ) : (
                                            <Trans>days late</Trans>
                                          )}
                                        </Text>
                                      </>
                                    );
                                  } else {
                                    return (
                                      <Text color="gray.500">
                                        <Trans>On time</Trans>
                                      </Text>
                                    );
                                  }
                                })()}
                              </Flex>
                            </>
                          ) : null}

                          {/* Scheduled Due */}
                          {item.scheduled_due_date ? (
                            <Flex align="center" gap={2} fontSize="sm" color="gray.500" mt={2}>
                              <Calendar size={14} />
                              <Text>
                                <Trans>Scheduled Due:</Trans>{' '}
                                {djs(item.scheduled_due_date).format('DD MMM YYYY')}
                              </Text>
                            </Flex>
                          ) : null}

                          {/* Actual Completion with comparison */}
                          {isCompleted ? (
                            <>
                              <Flex align="center" gap={2} fontSize="sm" color="gray.600">
                                <Clock size={14} />
                                <Text>
                                  <Trans>Actually Completed:</Trans>{' '}
                                  {djs(item.completed_at).format('DD MMM YYYY, hh:mm A')}
                                </Text>
                              </Flex>
                              {item.scheduled_due_date ? (
                                <Flex align="center" gap={2} fontSize="xs" ml={5}>
                                  {(() => {
                                    const daysDiff = djs(item.completed_at).diff(
                                      djs(item.scheduled_due_date),
                                      'day',
                                    );
                                    if (daysDiff < 0) {
                                      return (
                                        <>
                                          <TrendingUp size={12} color="green" />
                                          <Text color="green.600">
                                            {Math.abs(daysDiff)}{' '}
                                            {Math.abs(daysDiff) === 1 ? (
                                              <Trans>day early</Trans>
                                            ) : (
                                              <Trans>days early</Trans>
                                            )}
                                          </Text>
                                        </>
                                      );
                                    } else if (daysDiff > 0) {
                                      return (
                                        <>
                                          <TrendingDown size={12} color="red" />
                                          <Text color="red.600">
                                            {daysDiff}{' '}
                                            {daysDiff === 1 ? (
                                              <Trans>day late</Trans>
                                            ) : (
                                              <Trans>days late</Trans>
                                            )}
                                          </Text>
                                        </>
                                      );
                                    } else {
                                      return (
                                        <Text color="gray.500">
                                          <Trans>On time</Trans>
                                        </Text>
                                      );
                                    }
                                  })()}
                                </Flex>
                              ) : null}
                            </>
                          ) : null}

                          {/* Duration */}
                          {isStarted && isCompleted ? (
                            <Text fontSize="xs" color="fg.muted" ml={5} mt={2}>
                              <Trans>Duration:</Trans>{' '}
                              {djs(item.completed_at).diff(djs(item.started_at), 'minute')}{' '}
                              <Trans>minutes</Trans>
                            </Text>
                          ) : null}
                        </>
                      )}
                    </VStack>
                  </Box>
                </Flex>
              </Box>
            );
          })}
        </VStack>
      </Box>
    </Box>
  );
}
